import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import { computed, type ReadonlySignal } from '@preact/signals-core';
import {
  resolveTemporalProfileColumn,
  TEMPORAL_PROFILE_ROLE_PRIMARY_TYPE,
  type TemporalProfileRole,
  temporalProfileRoleAcceptsPropertyType,
} from '@streamlain/database-identity';

import type { GroupBy } from '../../core/common/types.js';
import type { DataSourceWindowRequest } from '../../core/data-source/base.js';
import { clampDataSourceWindowOffset } from '../../core/data-source/window.js';
import { FilterTrait, filterTraitKey } from '../../core/filter/trait.js';
import type { FilterGroup } from '../../core/filter/types.js';
import {
  emptyFilterGroup,
  normalizeFilterGroup,
} from '../../core/filter/utils.js';
import {
  GroupTrait,
  groupTraitKey,
  sortByManually,
} from '../../core/group-by/trait.js';
import { SortManager, sortTraitKey } from '../../core/sort/manager.js';
import type { Sort } from '../../core/sort/types.js';
import { PropertyBase } from '../../core/view-manager/property.js';
import {
  SingleViewBase,
  type SingleViewPropertiesWindow,
} from '../../core/view-manager/single-view.js';
import { defaultTemporalProfileColumnName } from '../temporal-profile.js';
import type {
  CalendarIcsSubscription,
  CalendarMode,
  CalendarViewData,
} from './define.js';
import {
  type CalendarDisplaySettings,
  decodeCalendarDisplaySettings,
  normalizeCalendarDisplaySettings,
} from './pc/calendar-display-policy.js';

/**
 * The column of every calendar role as `ensureCalendarColumns` resolved or
 * created it. Writers use these ids: the view's role signals follow only once
 * the column change is published.
 */
export type CalendarColumnIds = {
  readonly [Key in
    | 'titleColumn'
    | 'allDayCol'
    | 'assigneeCol'
    | 'attendeesCol'
    | 'calendarIdCol'
    | 'dependenciesCol'
    | 'descriptionCol'
    | 'endDateCol'
    | 'locationPhysicalCol'
    | 'locationVirtualCol'
    | 'progressCol'
    | 'recurrenceExdateCol'
    | 'recurrenceIdCol'
    | 'recurrenceCol'
    | 'reminderCol'
    | 'startDateCol'
    | 'statusCol'
    | 'timezoneCol'
    | 'visibilityCol'
    | 'icsSequenceCol'
    | 'icsUidCol'
    | 'invitationMethodCol'
    | 'invitationStateCol'
    | 'sourceMailAccountIdCol'
    | 'sourceMailAttachmentBlobKeyCol'
    | 'sourceMailMessageIdCol'
    | 'sourceTypeCol']: string | undefined;
};

export type CalendarColumnRole =
  | 'start'
  | 'end'
  | 'allDay'
  | 'timezone'
  | 'recurrence'
  | 'recurrenceExdate'
  | 'recurrenceId'
  | 'calendarId'
  | 'locationPhysical'
  | 'locationVirtual'
  | 'description'
  | 'attendees'
  | 'reminder'
  | 'visibility'
  | 'status';

const REQUIRED_COLUMN_ROLES = new Set<CalendarColumnRole>(['start', 'end']);

type CalendarColumnData = CalendarViewData['columns'][number];

export function calendarRoleAcceptsPropertyType(
  role: CalendarColumnRole,
  propertyType: string | undefined
): boolean {
  return temporalProfileRoleAcceptsPropertyType(role, propertyType);
}

export class CalendarSingleView extends SingleViewBase<CalendarViewData> {
  readonly displaySettings$: ReadonlySignal<CalendarDisplaySettings> = computed(
    () => normalizeCalendarDisplaySettings(this.data$.value?.displaySettings)
  );
  setDisplaySettings(settings: CalendarDisplaySettings): void {
    if (this.readonly$.value) return;
    const value = decodeCalendarDisplaySettings(settings);
    if (!value) throw new Error('Invalid calendar display settings');
    this.dataUpdate(() => ({ displaySettings: value }));
  }

  propertiesRaw$: ReadonlySignal<CalendarColumn[]> = computed(() => {
    const needShow = new Set(this.getBoundedPropertyIdsForView());
    const result: string[] = [];
    this.data$.value?.columns.forEach(column => {
      if (needShow.has(column.id)) {
        result.push(column.id);
        needShow.delete(column.id);
      }
    });
    result.push(...needShow);
    return result.map(id => this.propertyGetOrCreate(id));
  });

  properties$: ReadonlySignal<CalendarColumn[]> = computed(() =>
    this.propertiesRaw$.value.filter(property => !property.hide$.value)
  );

  detailProperties$: ReadonlySignal<CalendarColumn[]> = computed(() =>
    this.propertiesRaw$.value.filter(
      property => property.type$.value !== 'title'
    )
  );

  filter$ = computed(() => normalizeFilterGroup(this.data$.value?.filter));

  filterTrait = this.traitSet(
    filterTraitKey,
    new FilterTrait(this.filter$, this, {
      filterSet: filter => {
        this.dataUpdate(() => ({ filter }));
      },
    })
  );

  private readonly sortList$ = computed(() => this.data$.value?.sort);

  private readonly sortManager = this.traitSet(
    sortTraitKey,
    new SortManager(this.sortList$, this, {
      setSortList: sortList => {
        this.dataUpdate(data => ({
          sort: {
            ...data.sort,
            ...sortList,
          },
        }));
      },
    })
  );

  groupBy$ = computed(() => this.data$.value?.groupBy);

  protected override getProjectionFilter(): FilterGroup | undefined {
    return this.filter$.value;
  }

  protected override getProjectionGroupBy(): GroupBy | undefined {
    return this.groupBy$.value;
  }

  protected override getProjectionSort(): Sort | undefined {
    return this.sortList$.value;
  }

  groupTrait = this.traitSet(
    groupTraitKey,
    new GroupTrait(this.groupBy$, this, {
      groupBySet: groupBy => {
        this.dataUpdate(() => ({ groupBy }));
      },
      sortGroup: (ids, asc) => {
        const sorted = sortByManually(
          ids,
          value => value,
          this.data$.value?.groupProperties?.map(value => value.key) ?? []
        );
        return asc === false ? sorted.reverse() : sorted;
      },
      sortRow: (key, rows) => {
        const property = this.data$.value?.groupProperties?.find(
          value => value.key === key
        );
        return sortByManually(
          rows,
          row => row.rowId,
          property?.manuallyCardSort ?? []
        );
      },
      changeGroupSort: keys => {
        const map = new Map(
          this.data$.value?.groupProperties?.map(value => [value.key, value])
        );
        this.dataUpdate(() => ({
          groupProperties: keys.map(key => {
            return (
              map.get(key) ?? {
                key,
                hide: false,
                manuallyCardSort: [],
              }
            );
          }),
        }));
      },
      changeRowSort: (groupKeys, groupKey, keys) => {
        const map = new Map(
          this.data$.value?.groupProperties?.map(value => [value.key, value])
        );
        this.dataUpdate(() => ({
          groupProperties: groupKeys.map(key => {
            if (key === groupKey) {
              const group = map.get(key);
              return group
                ? { ...group, manuallyCardSort: keys }
                : { key, hide: false, manuallyCardSort: keys };
            }
            return (
              map.get(key) ?? {
                key,
                hide: false,
                manuallyCardSort: [],
              }
            );
          }),
        }));
      },
      changeGroupHide: (key, hide) => {
        const list = [...(this.data$.value?.groupProperties ?? [])];
        const index = list.findIndex(value => value.key === key);
        if (index >= 0) {
          const target = list[index];
          if (!target) return;
          list[index] = { ...target, hide };
        } else {
          list.push({ key, hide, manuallyCardSort: [] });
        }
        this.dataUpdate(() => ({ groupProperties: list }));
      },
    })
  );

  mainProperties$ = computed(() => {
    const saved = this.data$.value?.header;
    return {
      titleColumn:
        saved?.titleColumn ??
        this.getBoundedRawPropertiesWindow().properties.find(
          property => property.type$.value === 'title'
        )?.id,
      iconColumn: saved?.iconColumn ?? 'type',
    };
  });

  readonly$ = computed(() => this.manager.readonly$.value);

  startDateCol$ = computed(() => this.data$.value?.startDateCol);
  endDateCol$ = computed(() => this.data$.value?.endDateCol);
  allDayCol$ = computed(() => this.data$.value?.allDayCol);
  timezoneCol$ = computed(() => this.data$.value?.timezoneCol);
  recurrenceCol$ = computed(() => this.data$.value?.recurrenceCol);
  recurrenceExdateCol$ = computed(() => this.data$.value?.recurrenceExdateCol);
  recurrenceIdCol$ = computed(() => this.data$.value?.recurrenceIdCol);
  calendarIdCol$ = computed(() => this.data$.value?.calendarIdCol);
  locationPhysicalCol$ = computed(() => this.data$.value?.locationPhysicalCol);
  locationVirtualCol$ = computed(() => this.data$.value?.locationVirtualCol);
  descriptionCol$ = computed(() => this.data$.value?.descriptionCol);
  attendeesCol$ = computed(() => this.data$.value?.attendeesCol);
  reminderCol$ = computed(() => this.data$.value?.reminderCol);
  visibilityCol$ = computed(() => this.data$.value?.visibilityCol);
  statusCol$ = computed(() => this.data$.value?.statusCol);
  assigneeCol$ = computed(() => this.data$.value?.assigneeCol);
  progressCol$ = computed(() => this.data$.value?.progressCol);
  dependenciesCol$ = computed(() => this.data$.value?.dependenciesCol);
  icsSequenceCol$ = computed(() => this.data$.value?.icsSequenceCol);
  icsUidCol$ = computed(() => this.data$.value?.icsUidCol);
  invitationMethodCol$ = computed(() => this.data$.value?.invitationMethodCol);
  invitationStateCol$ = computed(() => this.data$.value?.invitationStateCol);
  sourceMailAccountIdCol$ = computed(
    () => this.data$.value?.sourceMailAccountIdCol
  );
  sourceMailAttachmentBlobKeyCol$ = computed(
    () => this.data$.value?.sourceMailAttachmentBlobKeyCol
  );
  sourceMailMessageIdCol$ = computed(
    () => this.data$.value?.sourceMailMessageIdCol
  );
  sourceTypeCol$ = computed(() => this.data$.value?.sourceTypeCol);
  calendarMode$ = computed<CalendarMode>(
    () => this.data$.value?.calendarMode ?? 'week'
  );
  calendarCursorTime$ = computed(() => this.data$.value?.calendarCursorTime);
  visibleCalendarIds$ = computed(
    () => this.data$.value?.visibleCalendarIds ?? []
  );
  calendarIcsSubscriptions$ = computed(
    () => this.data$.value?.icsSubscriptions ?? []
  );

  isConfigured$ = computed(
    () =>
      this.isRoleColumnUsable('start', this.startDateCol$.value) &&
      this.isRoleColumnUsable('end', this.endDateCol$.value)
  );

  missingColumnRoles$ = computed<readonly CalendarColumnRole[]>(() => {
    const pairs: ReadonlyArray<[CalendarColumnRole, string | undefined]> = [
      ['start', this.startDateCol$.value],
      ['end', this.endDateCol$.value],
      ['allDay', this.allDayCol$.value],
      ['timezone', this.timezoneCol$.value],
      ['recurrence', this.recurrenceCol$.value],
      ['recurrenceExdate', this.recurrenceExdateCol$.value],
      ['recurrenceId', this.recurrenceIdCol$.value],
      ['calendarId', this.calendarIdCol$.value],
      ['locationPhysical', this.locationPhysicalCol$.value],
      ['locationVirtual', this.locationVirtualCol$.value],
      ['description', this.descriptionCol$.value],
      ['attendees', this.attendeesCol$.value],
      ['reminder', this.reminderCol$.value],
      ['visibility', this.visibilityCol$.value],
      ['status', this.statusCol$.value],
    ];
    return pairs
      .filter(([role, id]) => !this.isRoleColumnUsable(role, id))
      .map(([role]) => role);
  });

  requiredMissingColumnRoles$ = computed<readonly CalendarColumnRole[]>(() =>
    this.missingColumnRoles$.value.filter(role =>
      REQUIRED_COLUMN_ROLES.has(role)
    )
  );

  optionalMissingColumnRoles$ = computed<readonly CalendarColumnRole[]>(() =>
    this.missingColumnRoles$.value.filter(
      role => !REQUIRED_COLUMN_ROLES.has(role)
    )
  );

  private getRawPropertiesWindowForRequest(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const sourceWindow = this.dataSource.getPropertyIdsWindow(request);
    const needShow = new Set(sourceWindow.propertyIds);
    const orderedIds: string[] = [];
    this.data$.value?.columns.forEach(column => {
      if (needShow.has(column.id)) {
        orderedIds.push(column.id);
        needShow.delete(column.id);
      }
    });
    orderedIds.push(...needShow);
    const windowProperties = orderedIds.map(id => this.propertyGetOrCreate(id));
    return {
      offset: sourceWindow.offset,
      limit: sourceWindow.limit,
      totalCount: sourceWindow.totalCount,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  override getPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const rawWindow = this.getRawPropertiesWindowForRequest(request);
    const windowProperties = rawWindow.properties.filter(
      property => !property.hide$.value
    );
    return {
      ...rawWindow,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  override getBoundedPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getPropertiesWindow({ offset, limit });
  }

  override getRawPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    return this.getRawPropertiesWindowForRequest(request);
  }

  override getBoundedRawPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getRawPropertiesWindow({ offset, limit });
  }

  get type(): string {
    return this.data$.value?.mode ?? 'calendar';
  }

  get view() {
    return this.data$.value;
  }

  get filter(): FilterGroup {
    return this.view?.filter ?? emptyFilterGroup;
  }

  isShow(rowId: string, context?: { nowMs: number }): boolean {
    return this.isRowVisibleByFilter(rowId, this.filter$.value, context);
  }

  protected override rowsMapping(
    rows: Parameters<SingleViewBase['rowsMapping']>[0]
  ): ReturnType<SingleViewBase['rowsMapping']> {
    return this.sortManager.sort(super.rowsMapping(rows));
  }

  propertyGetOrCreate(propertyId: string): CalendarColumn {
    return new CalendarColumn(this, propertyId);
  }

  setCalendarMode(mode: CalendarMode): void {
    if (this.readonly$.value) return;
    this.dataUpdate(() => ({ calendarMode: mode }));
  }

  setCalendarCursorTime(cursorTime: number | undefined): void {
    if (this.readonly$.value) return;
    this.dataUpdate(() => ({ calendarCursorTime: cursorTime }));
  }

  setVisibleCalendarIds(ids: readonly string[]): void {
    if (this.readonly$.value) return;
    this.dataUpdate(() => ({ visibleCalendarIds: [...new Set(ids)] }));
  }

  setCalendarIcsSubscriptions(
    subscriptions: readonly CalendarIcsSubscription[]
  ): void {
    if (this.readonly$.value) return;
    const seen = new Set<string>();
    this.dataUpdate(() => ({
      icsSubscriptions: subscriptions.flatMap(subscription => {
        if (seen.has(subscription.id)) return [];
        seen.add(subscription.id);
        return [
          {
            enabled: subscription.enabled !== false,
            id: subscription.id,
            name: subscription.name,
            url: subscription.url,
          },
        ];
      }),
    }));
  }

  setStartDateCol(propertyId: string | undefined): void {
    if (this.readonly$.value) return;
    this.dataUpdate(data =>
      propertyId && !this.isRoleColumnUsable('start', propertyId, data)
        ? {}
        : { startDateCol: propertyId }
    );
    if (propertyId) this.ensureDateTimeProperty(propertyId);
  }

  setEndDateCol(propertyId: string | undefined): void {
    if (this.readonly$.value) return;
    this.dataUpdate(data =>
      propertyId && !this.isRoleColumnUsable('end', propertyId, data)
        ? {}
        : { endDateCol: propertyId }
    );
    if (propertyId) this.ensureDateTimeProperty(propertyId);
  }

  setRoleColumn(
    role: CalendarColumnRole,
    propertyId: string | undefined
  ): void {
    if (this.readonly$.value) return;
    const patch = {
      allDay: { allDayCol: propertyId },
      attendees: { attendeesCol: propertyId },
      description: { descriptionCol: propertyId },
      end: { endDateCol: propertyId },
      locationPhysical: { locationPhysicalCol: propertyId },
      locationVirtual: { locationVirtualCol: propertyId },
      recurrence: { recurrenceCol: propertyId },
      recurrenceExdate: { recurrenceExdateCol: propertyId },
      recurrenceId: { recurrenceIdCol: propertyId },
      calendarId: { calendarIdCol: propertyId },
      reminder: { reminderCol: propertyId },
      start: { startDateCol: propertyId },
      status: { statusCol: propertyId },
      timezone: { timezoneCol: propertyId },
      visibility: { visibilityCol: propertyId },
    } satisfies Record<CalendarColumnRole, Partial<CalendarViewData>>;
    this.dataUpdate(data =>
      propertyId && !this.isRoleColumnUsable(role, propertyId, data)
        ? {}
        : patch[role]
    );
    if ((role === 'start' || role === 'end') && propertyId) {
      this.ensureDateTimeProperty(propertyId);
    }
  }

  ensureCalendarColumns(): CalendarColumnIds {
    if (this.readonly$.value) {
      throw new Error('A read-only calendar view cannot create its columns.');
    }
    const titleColumn =
      this.mainProperties$.value.titleColumn ?? this.findProperty('title');
    const startDateCol = this.resolveOrCreateTemporalRole(
      'start',
      this.startDateCol$.value
    );
    const endDateCol = this.resolveOrCreateTemporalRole(
      'end',
      this.endDateCol$.value,
      startDateCol ? [startDateCol] : []
    );
    const allDayCol = this.resolveOrCreateTemporalRole(
      'allDay',
      this.allDayCol$.value
    );
    const timezoneCol = this.resolveOrCreateTemporalRole(
      'timezone',
      this.timezoneCol$.value
    );
    const recurrenceCol = this.resolveOrCreateTemporalRole(
      'recurrence',
      this.recurrenceCol$.value
    );
    const recurrenceExdateCol = this.resolveOrCreateTemporalRole(
      'recurrenceExdate',
      this.recurrenceExdateCol$.value
    );
    const recurrenceIdCol = this.resolveOrCreateTemporalRole(
      'recurrenceId',
      this.recurrenceIdCol$.value
    );
    const calendarIdCol = this.resolveOrCreateTemporalRole(
      'calendarId',
      this.calendarIdCol$.value
    );
    const locationPhysicalCol = this.resolveOrCreateTemporalRole(
      'locationPhysical',
      this.locationPhysicalCol$.value
    );
    const locationVirtualCol = this.resolveOrCreateTemporalRole(
      'locationVirtual',
      this.locationVirtualCol$.value
    );
    const attendeesCol = this.resolveOrCreateTemporalRole(
      'attendees',
      this.attendeesCol$.value
    );
    const reminderCol = this.resolveOrCreateTemporalRole(
      'reminder',
      this.reminderCol$.value
    );
    const descriptionCol = this.resolveOrCreateTemporalRole(
      'description',
      this.descriptionCol$.value
    );
    const visibilityCol = this.resolveOrCreateTemporalRole(
      'visibility',
      this.visibilityCol$.value
    );
    const statusCol = this.resolveOrCreateTemporalRole(
      'status',
      this.statusCol$.value
    );
    const assigneeCol = this.resolveOrCreateTemporalRole(
      'assignee',
      this.assigneeCol$.value
    );
    const progressCol = this.resolveOrCreateTemporalRole(
      'progress',
      this.progressCol$.value
    );
    const dependenciesCol = this.resolveOrCreateTemporalRole(
      'dependencies',
      this.dependenciesCol$.value
    );
    const invitationMetadata = this.ensureInvitationMetadataColumns();

    if (startDateCol) this.ensureDateTimeProperty(startDateCol);
    if (endDateCol) this.ensureDateTimeProperty(endDateCol);

    const ids: CalendarColumnIds = {
      allDayCol,
      assigneeCol,
      attendeesCol,
      calendarIdCol,
      dependenciesCol,
      descriptionCol,
      endDateCol,
      locationPhysicalCol,
      locationVirtualCol,
      progressCol,
      recurrenceExdateCol,
      recurrenceIdCol,
      recurrenceCol,
      reminderCol,
      startDateCol,
      statusCol,
      timezoneCol,
      titleColumn,
      visibilityCol,
      icsSequenceCol: invitationMetadata.icsSequenceCol,
      icsUidCol: invitationMetadata.icsUidCol,
      invitationMethodCol: invitationMetadata.invitationMethodCol,
      invitationStateCol: invitationMetadata.invitationStateCol,
      sourceMailAccountIdCol: invitationMetadata.sourceMailAccountIdCol,
      sourceMailAttachmentBlobKeyCol:
        invitationMetadata.sourceMailAttachmentBlobKeyCol,
      sourceMailMessageIdCol: invitationMetadata.sourceMailMessageIdCol,
      sourceTypeCol: invitationMetadata.sourceTypeCol,
    };
    const { titleColumn: headerTitleColumn, ...roleColumns } = ids;
    const data = this.data$.value;
    const unchanged =
      data !== undefined &&
      data.header?.titleColumn === headerTitleColumn &&
      data.header?.iconColumn === 'type' &&
      Object.entries(roleColumns).every(
        ([key, id]) => data[key as keyof typeof roleColumns] === id
      );
    if (!unchanged)
      this.dataUpdate(() => ({
        ...roleColumns,
        header: {
          titleColumn: headerTitleColumn,
          iconColumn: 'type',
        },
      }));
    return ids;
  }

  private ensureInvitationMetadataColumns(): Pick<
    CalendarViewData,
    | 'icsSequenceCol'
    | 'icsUidCol'
    | 'invitationMethodCol'
    | 'invitationStateCol'
    | 'sourceMailAccountIdCol'
    | 'sourceMailAttachmentBlobKeyCol'
    | 'sourceMailMessageIdCol'
    | 'sourceTypeCol'
  > {
    const specs = {
      icsSequenceCol: {
        currentId: this.icsSequenceCol$.value,
        name: 'ICS sequence',
        type: 'number',
      },
      icsUidCol: {
        currentId: this.icsUidCol$.value,
        name: 'ICS UID',
        type: 'rich-text',
      },
      invitationMethodCol: {
        currentId: this.invitationMethodCol$.value,
        name: 'Invitation method',
        type: 'rich-text',
      },
      invitationStateCol: {
        currentId: this.invitationStateCol$.value,
        name: 'Invitation state',
        type: 'select',
      },
      sourceMailAccountIdCol: {
        currentId: this.sourceMailAccountIdCol$.value,
        name: 'Source mail account',
        type: 'rich-text',
      },
      sourceMailAttachmentBlobKeyCol: {
        currentId: this.sourceMailAttachmentBlobKeyCol$.value,
        name: 'Source mail attachment',
        type: 'rich-text',
      },
      sourceMailMessageIdCol: {
        currentId: this.sourceMailMessageIdCol$.value,
        name: 'Source mail message',
        type: 'rich-text',
      },
      sourceTypeCol: {
        currentId: this.sourceTypeCol$.value,
        name: 'Source type',
        type: 'rich-text',
      },
    } as const;
    const result: Partial<Record<keyof typeof specs, string>> = {};
    for (const [key, spec] of Object.entries(specs) as Array<
      [keyof typeof specs, (typeof specs)[keyof typeof specs]]
    >) {
      const currentId = spec.currentId;
      const propertyId =
        currentId &&
        this.dataSource.getAllPropertyIds().includes(currentId) &&
        this.dataSource.propertyTypeGet(currentId) === spec.type
          ? currentId
          : this.createProperty(spec.type, spec.name);
      if (!propertyId) continue;
      this.propertyGetOrCreate(propertyId).hideSet(true);
      result[key] = propertyId;
    }
    return result;
  }

  /**
   * Completes the role columns and resolves once every one of them is live.
   * A new column only gets its type when its schema change is acknowledged;
   * cells written before that have no type to convert their value with.
   * A rejected schema change rejects here.
   */
  async ensureCalendarColumnsLive(): Promise<CalendarColumnIds> {
    const ids = this.ensureCalendarColumns();
    const pending = Object.values(ids).some(
      id =>
        id !== undefined && this.dataSource.propertyTypeGet(id) === undefined
    );
    if (pending) await this.dataSource.flushLocalWrites();
    return ids;
  }

  private resolveOrCreateTemporalRole(
    role: TemporalProfileRole,
    currentId: string | undefined,
    excludeIds: readonly string[] = []
  ): string | undefined {
    return (
      resolveTemporalProfileColumn({
        currentId,
        dataSource: this.dataSource,
        excludeIds,
        role,
        selfBlockId: this.selfBlockId,
        views: this.dataSource.viewDataList$.value,
      }) ?? this.createTemporalProfileProperty(role)
    );
  }

  private createTemporalProfileProperty(
    role: TemporalProfileRole
  ): string | undefined {
    if (role === 'title') return this.mainProperties$.value.titleColumn;
    if (role === 'dependencies') {
      return this.createTemporalProfileRelation(role);
    }
    return this.createProperty(
      TEMPORAL_PROFILE_ROLE_PRIMARY_TYPE[role],
      defaultTemporalProfileColumnName(role)
    );
  }

  private createTemporalProfileRelation(
    role: TemporalProfileRole
  ): string | undefined {
    const selfBlockId = this.selfBlockId;
    if (!selfBlockId) return undefined;
    const forwardId = this.createProperty(
      'relation',
      defaultTemporalProfileColumnName(role)
    );
    if (!forwardId) return undefined;
    const backlinkId = this.createProperty('relation', 'Dependents');
    const dataSource = this.dataSource as unknown as {
      propertyDataSet?: (id: string, data: Record<string, unknown>) => void;
    };
    const titleCol = this.mainProperties$.value.titleColumn ?? '';
    dataSource.propertyDataSet?.(forwardId, {
      targetDatabaseId: selfBlockId,
      targetFieldId: titleCol,
      cardinality: 'many',
      inverseFieldId: backlinkId,
      inverseCardinality: 'many',
    });
    if (backlinkId) {
      dataSource.propertyDataSet?.(backlinkId, {
        targetDatabaseId: selfBlockId,
        targetFieldId: titleCol,
        cardinality: 'many',
        inverseFieldId: forwardId,
        inverseCardinality: 'many',
      });
    }
    return forwardId;
  }

  private get selfBlockId(): string {
    return (
      (this.manager.dataSource as unknown as { blockId?: string }).blockId ?? ''
    );
  }

  private findProperty(
    type: string,
    namePattern?: RegExp,
    excludeId?: string
  ): string | undefined {
    return this.dataSource.getAllPropertyIds().find(id => {
      if (id === excludeId) return false;
      if (this.dataSource.propertyTypeGet(id) !== type) return false;
      return namePattern
        ? namePattern.test(this.dataSource.propertyNameGet(id))
        : true;
    });
  }

  private createProperty(type: string, name: string): string | undefined {
    return this.propertyAdd('end', { type, name });
  }

  private isRoleColumnUsable(
    role: CalendarColumnRole,
    propertyId: string | undefined,
    data: CalendarViewData | undefined = this.view
  ): boolean {
    if (!propertyId || !this.dataSource.propertyTypeGet(propertyId))
      return false;
    if (role === 'start' && propertyId === data?.endDateCol) {
      return false;
    }
    if (role === 'end' && propertyId === data?.startDateCol) {
      return false;
    }
    return calendarRoleAcceptsPropertyType(
      role,
      this.dataSource.propertyTypeGet(propertyId)
    );
  }

  private ensureDateTimeProperty(propertyId: string): void {
    if (
      this.dataSource.propertyTypeGet(propertyId) !== 'date' ||
      this.dataSource.propertyDataGet(propertyId).includeTime === true
    )
      return;
    this.propertyGetOrCreate(propertyId).dataUpdate(data => ({
      ...data,
      includeTime: true,
    }));
  }

  isRoleColumn(propertyId: string): boolean {
    return (
      this.startDateCol$.value === propertyId ||
      this.endDateCol$.value === propertyId ||
      this.allDayCol$.value === propertyId ||
      this.timezoneCol$.value === propertyId ||
      this.recurrenceCol$.value === propertyId ||
      this.recurrenceExdateCol$.value === propertyId ||
      this.recurrenceIdCol$.value === propertyId ||
      this.calendarIdCol$.value === propertyId ||
      this.locationPhysicalCol$.value === propertyId ||
      this.locationVirtualCol$.value === propertyId ||
      this.descriptionCol$.value === propertyId ||
      this.attendeesCol$.value === propertyId ||
      this.reminderCol$.value === propertyId ||
      this.visibilityCol$.value === propertyId ||
      this.statusCol$.value === propertyId
    );
  }
}

export class CalendarColumn extends PropertyBase {
  override move(position: InsertToPosition): void {
    this.calendarView.dataUpdate(data =>
      this.calendarView.placeProperty(data, this.id, position)
    );
  }

  override hideSet(hide: boolean): void {
    this.viewDataUpdate(data => ({ ...data, hide }));
  }

  hide$ = computed(() => {
    if (this.type$.value === 'title') return false;
    const hideFromViewData = this.viewData$.value?.hide;
    if (hideFromViewData != null) return hideFromViewData;
    return !this.calendarView.isRoleColumn(this.id);
  });

  width$ = computed<number | undefined>(() => this.viewData$.value?.width);

  widthSet(width: number | undefined): void {
    this.viewDataUpdate(data => ({ ...data, width }));
  }

  viewData$ = computed(() =>
    this.calendarView.data$.value?.columns.find(value => value.id === this.id)
  );

  viewDataUpdate(
    updater: (viewData: CalendarColumnData) => Partial<CalendarColumnData>
  ): void {
    this.calendarView.dataUpdate(data => {
      const existing = data.columns.find(value => value.id === this.id);
      if (existing) {
        return {
          ...data,
          columns: data.columns.map(value =>
            value.id === this.id ? { ...value, ...updater(value) } : value
          ),
        };
      }
      const newEntry: CalendarColumnData = {
        id: this.id,
        hide: false,
        ...updater({ id: this.id, hide: false }),
      };
      return {
        ...data,
        columns: [...data.columns, newEntry],
      };
    });
  }

  constructor(
    private readonly calendarView: CalendarSingleView,
    columnId: string
  ) {
    super(calendarView, columnId);
  }
}
